import { isEqual } from "lodash-es"

type CollectionIdentity = { id?: string; _ref_id?: string }

function indexByIdentity<T extends CollectionIdentity>(collections: T[]) {
  const byID = new Map(collections.filter((c) => c.id).map((c) => [c.id, c]))
  const byRef = new Map(
    collections.filter((c) => c._ref_id).map((c) => [c._ref_id, c])
  )
  return (collection: T) =>
    (collection.id ? byID.get(collection.id) : undefined) ??
    (collection._ref_id ? byRef.get(collection._ref_id) : undefined)
}

/**
 * Apply a server snapshot without replacing roots changed since the fetch began.
 * A root includes its folders and requests. Keep changed roots by reference:
 * outstanding create mutations assign server IDs directly to those objects.
 * Unchanged cached roots remain server-authoritative, including server deletions.
 */
export function reconcileCollectionLoad<T extends CollectionIdentity>(
  before: T[],
  current: T[],
  incoming: T[]
): T[] {
  if (isEqual(before, current)) return incoming

  const findBefore = indexByIdentity(before)
  const findCurrent = indexByIdentity(current)
  const findIncoming = indexByIdentity(incoming)
  const matched = new Set<T>()
  const result: T[] = []

  // Preserve local ordering while writes may still be in flight.
  for (const collection of current) {
    const original = findBefore(collection)
    const remote = findIncoming(collection)
    if (remote) matched.add(remote)
    if (!original || !isEqual(original, collection)) {
      result.push(collection)
    } else if (remote) {
      result.push(remote)
    }
  }

  for (const collection of incoming) {
    if (matched.has(collection)) continue
    const original = findBefore(collection)
    // A removal after the fetch started must not be undone by its response.
    if (original && !findCurrent(original)) continue
    result.push(collection)
  }
  return result
}
